import { Dispatch, SetStateAction } from 'react';
import clsx from 'clsx';

import {
  formatADOTUnitCost,
  isADOTBaseUnit,
  multiplyADOTConversion,
} from '@atom/components/common/requests/adotReplenishmentUtilities';
import { canEditADOTReplenishment } from '@atom/components/common/requests/adotRequestPermissions';
import { Icon, IconButton } from '@atom/mui';
import { EditIcon } from '@atom/mui/icons';
import {
  AssetRequestPurchaseUnit,
  Replenishment,
} from '@atom/types/assetRequest';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import {
  getDisplayDateTime,
  setDisplayDate,
} from '@atom/utilities/timeUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ADOTConvertedQuantity from './ADOTConvertedQuantity';

import classes from './adotView.module.css';

type ADOTReplenishmentCardProps = {
  replenishment: Replenishment;
  purchaseUnit: AssetRequestPurchaseUnit;
  editId: string;
  setEditId: Dispatch<SetStateAction<string>>;
};

const ADOTReplenishmentCard = ({
  replenishment,
  purchaseUnit,
  editId,
  setEditId,
}: ADOTReplenishmentCardProps) => {
  const createdByText = `Created by ${getUserFullName(
    replenishment?.createdBy,
  )} on ${getDisplayDateTime(replenishment?.createdDate)}`;

  const updatedByText = `Last edited by ${getUserFullName(
    replenishment?.updatedBy,
  )} on ${getDisplayDateTime(replenishment?.updatedDate)}`;

  const showUpdatedByText =
    replenishment?.createdDate !== replenishment?.updatedDate &&
    !isNilOrEmpty(replenishment?.updatedDate);

  const showEditButton = isNilOrEmpty(editId) && canEditADOTReplenishment();

  return (
    <div
      className={clsx(classes.formContainer, classes.replenishmentCard)}
      data-cy={`adotReplenishmentCard-${replenishment?.name}`}
    >
      <div className={classes.replenishmentTitle}>
        <div>
          {replenishment?.name}
          {showUpdatedByText && (
            <span className={classes.editedSubtext}>(edited)</span>
          )}
        </div>
        {showEditButton && (
          <IconButton
            size="small"
            onClick={() => setEditId(replenishment?.id)}
            data-cy={`adotReplenishmentEditButton-${replenishment?.name}`}
          >
            <Icon icon={EditIcon} />
          </IconButton>
        )}
      </div>
      <div className={classes.replenishmentRow}>
        <div>
          <div className={classes.rowTitle}>Invoice Number</div>
          <div>{replenishment?.invoiceNumber}</div>
        </div>
        <div>
          <div className={classes.rowTitle}>Date Received</div>
          <div data-cy={`adotReplenishmentDateReceived-${replenishment?.name}`}>
            {setDisplayDate(replenishment?.dateReceived)}
          </div>
        </div>
        <div>
          <div className={classes.rowTitle}>Qty Received</div>
          <ADOTConvertedQuantity
            baseValue={replenishment?.quantityReceived}
            purchaseUnit={purchaseUnit}
          />
        </div>
        <div>
          <div className={classes.rowTitle}>Unit Cost</div>
          <div>
            {formatADOTUnitCost(
              multiplyADOTConversion(replenishment?.unitCost, purchaseUnit),
              purchaseUnit.unit,
            )}
          </div>
          {!isADOTBaseUnit(purchaseUnit) && (
            <div className={classes.subtext}>
              {formatADOTUnitCost(
                replenishment?.unitCost,
                purchaseUnit.baseUnit,
              )}
            </div>
          )}
        </div>
        <div>
          <div className={classes.rowTitle}>Total Cost</div>
          <div>{numberToLocaleString(replenishment?.totalCost)}</div>
        </div>
      </div>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Comments</div>
        <div>{replenishment?.comments || '-'}</div>
      </div>
      <div className={classes.createdSubtext}>{createdByText}</div>
      {showUpdatedByText && (
        <div className={classes.updatedSubtext}>{updatedByText}</div>
      )}
    </div>
  );
};

export default ADOTReplenishmentCard;
